import { useEffect, useMemo } from 'react'
import moment from 'moment'
import {
  useDashboardFetchWaves,
  useReportDashboardFetchWave,
  waveFetchState,
} from '@context/DashboardFetchWaveContext'
import type { RangeKey } from '@dataTypes/dashboard.types'
import {
  buildDashboardDetailHref,
  DASHBOARD_DETAIL_CONTEXT,
} from '@constants/Dashboard/dashboardDetailContext.util'
import { DASHBOARD_DETAIL_HREFS } from '@constants/Dashboard/dashboardDetailRoutes.constants'
import { useNotificationToast } from '@context/NotificationToastContext'
import { useAROver90Overview } from '@hooks/dashboard/useAROver90'
import {
  useDashboardPeriodFetch,
  useDashboardPeriodParams,
} from '@hooks/dashboard/useDashboardPeriod'
import { useInsuranceClaimsOverview } from '@hooks/dashboard/useInsuranceClaims'
import { usePatientsAgeOverview } from '@hooks/dashboard/usePatientsAge'
import { usePatientStatusOverview } from '@hooks/dashboard/usePatientStatus'
import { useRevenueOverview } from '@hooks/dashboard/useRevenue'
import { useScheduleOperationsOverview } from '@hooks/dashboard/useScheduleOperationsOverview'
import { useSeatUtilizationOverview } from '@hooks/dashboard/useSeatUtilization'
import { useClinicGoal } from '@hooks/dashboard/useClinicGoal'
import { getHygieneProductionOverview } from '@services/api/dashboard/finance.api'
import {
  CALENDAR_DATE_FORMAT,
  formatGoalCurrencyLabel,
} from '@utils/dashboardFinance.util'
import {
  buildNetHygieneProductionKpis,
  buildProductionPerVisitHygieneKpis,
} from '@utils/hygieneDashboard.util'
import { hasNonZeroDashboardValues } from '@utils/dashboardSectionEmpty.util'
import { createFailedToast } from '@utils/notificationToast.util'
import {
  buildOperationsSectionWaves,
  type OperationsSectionWaves,
} from './operationsSectionWaves'

export type { OperationsSectionWaves } from './operationsSectionWaves'

type UseOperationsDashboardOptions = {
  range: RangeKey
  date: string
  hiddenSections: Set<string>
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- inferred; exported hook is typed below
function useOperationsDashboardImpl({
  range,
  date,
  hiddenSections,
}: UseOperationsDashboardOptions) {
  const waves: OperationsSectionWaves =
    buildOperationsSectionWaves(hiddenSections)
  const openWaves = useDashboardFetchWaves()
  const revenueGate = waveFetchState(openWaves, waves.revenue)
  const hygieneGate = waveFetchState(openWaves, waves.hygiene)
  const arOver90Gate = waveFetchState(openWaves, waves.arOver90)
  const scheduleOperationsGate = waveFetchState(
    openWaves,
    waves.scheduleOperations,
  )
  const insuranceClaimsGate = waveFetchState(openWaves, waves.insuranceClaims)
  const patientsAgeGate = waveFetchState(openWaves, waves.patientsAge)
  const patientStatusGate = waveFetchState(openWaves, waves.patientStatus)
  const seatUtilizationGate = waveFetchState(openWaves, waves.seatUtilization)
  const revenueApi = useRevenueOverview({
    range,
    date,
    enabled: revenueGate.enabled,
    queued: revenueGate.queued,
  })
  const arOver90Api = useAROver90Overview({
    range,
    date,
    enabled: arOver90Gate.enabled,
    queued: arOver90Gate.queued,
  })
  const scheduleOperationsApi = useScheduleOperationsOverview(
    range,
    date,
    scheduleOperationsGate.enabled,
    scheduleOperationsGate.queued,
  )
  const insuranceClaimsApi = useInsuranceClaimsOverview({
    range,
    date,
    enabled: insuranceClaimsGate.enabled,
    queued: insuranceClaimsGate.queued,
  })
  const patientsAgeApi = usePatientsAgeOverview(
    range,
    undefined,
    patientsAgeGate.enabled,
    patientsAgeGate.queued,
  )
  const patientStatusApi = usePatientStatusOverview(
    range,
    undefined,
    patientStatusGate.enabled,
    patientStatusGate.queued,
  )
  const seatUtilizationApi = useSeatUtilizationOverview({
    range,
    date,
    enabled: seatUtilizationGate.enabled,
    queued: seatUtilizationGate.queued,
  })

  const hygieneQueryParams = useDashboardPeriodParams(range, date)
  const {
    data: hygieneData,
    loading: hygieneLoading,
    error: hygieneError,
  } = useDashboardPeriodFetch(
    hygieneQueryParams,
    getHygieneProductionOverview,
    {
      enabled: hygieneGate.enabled,
      queued: hygieneGate.queued,
    },
  )

  const { goal: clinicGoal, loading: clinicGoalLoading } = useClinicGoal({
    range,
    date,
    enabled: hygieneGate.enabled,
    queued: hygieneGate.queued,
  })

  const netHygieneMetric = useMemo(() => {
    const metric = buildNetHygieneProductionKpis(hygieneData)[0]
    if (!metric) return metric
    return {
      ...metric,
      rightLabel: formatGoalCurrencyLabel(
        clinicGoal?.net_hygiene_production_goal,
      ),
    }
  }, [hygieneData, clinicGoal])

  const productionPerVisitMetric = useMemo(() => {
    const metric = buildProductionPerVisitHygieneKpis(hygieneData)[0]
    if (!metric) return metric
    return {
      ...metric,
      rightLabel: formatGoalCurrencyLabel(
        clinicGoal?.production_per_hygiene_visit_goal,
      ),
    }
  }, [hygieneData, clinicGoal])

  const hygieneApi = {
    loading: hygieneLoading,
    error: hygieneError,
    hasPayload: hygieneData != null,
    hasNetHygieneData: hasNonZeroDashboardValues(
      hygieneData?.hygieneProduction.current,
    ),
    hasProductionPerVisitData: hasNonZeroDashboardValues(
      hygieneData?.productionPerHygieneVisit.current,
    ),
    netHygieneMetric,
    productionPerVisitMetric,
  }

  const hrefs = useMemo(() => {
    const detailParams = {
      context: DASHBOARD_DETAIL_CONTEXT.operations,
      range,
      date: moment(date, CALENDAR_DATE_FORMAT).format('YYYY-MM-DD'),
    }
    return {
      revenueOverview: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.revenueOverview,
        detailParams,
      ),
      netHygieneProduction: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.netHygieneProduction,
        detailParams,
      ),
      productionPerHygieneVisit: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.productionPerHygieneVisit,
        detailParams,
      ),
      arOver90Day: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.arOver90Day,
        detailParams,
      ),
      scheduleOperations: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.scheduleOperations,
        detailParams,
      ),
      insuranceClaims: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.insuranceClaims,
        detailParams,
      ),
      patientsAge: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.patientsAge,
        detailParams,
      ),
      patientStatus: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.patientStatus,
        detailParams,
      ),
      seatUtilization: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.seatUtilization,
        detailParams,
      ),
    }
  }, [range, date])

  const loading =
    revenueApi.loading ||
    hygieneApi.loading ||
    arOver90Api.loading ||
    scheduleOperationsApi.loading ||
    insuranceClaimsApi.loading ||
    patientsAgeApi.loading ||
    patientStatusApi.loading ||
    seatUtilizationApi.loading

  const hasError =
    (!revenueApi.loading && !!revenueApi.error) ||
    (!hygieneApi.loading && !!hygieneApi.error) ||
    (!arOver90Api.loading && !!arOver90Api.error) ||
    (!scheduleOperationsApi.loading && !!scheduleOperationsApi.error) ||
    (!insuranceClaimsApi.loading && !!insuranceClaimsApi.error) ||
    (!patientsAgeApi.loading && !!patientsAgeApi.error) ||
    (!patientStatusApi.loading && !!patientStatusApi.error) ||
    (!seatUtilizationApi.loading && !!seatUtilizationApi.error)

  useReportDashboardFetchWave(
    0,
    (waves.revenue === 0 && revenueApi.loading) ||
      (waves.scheduleOperations === 0 && scheduleOperationsApi.loading) ||
      (waves.insuranceClaims === 0 && insuranceClaimsApi.loading),
  )
  useReportDashboardFetchWave(
    1,
    (waves.hygiene === 1 && (hygieneApi.loading || clinicGoalLoading)) ||
      (waves.arOver90 === 1 && arOver90Api.loading) ||
      (waves.patientsAge === 1 && patientsAgeApi.loading) ||
      (waves.patientStatus === 1 && patientStatusApi.loading) ||
      (waves.seatUtilization === 1 && seatUtilizationApi.loading),
  )

  const { showToast } = useNotificationToast()

  useEffect(() => {
    if (!loading && hasError) {
      showToast(
        createFailedToast(
          'Failed to load metric',
          'Failed to load operations dashboard data. Please try again.',
        ),
      )
    }
  }, [loading, hasError, showToast])

  return {
    range,
    hrefs,
    loading,
    hasError,
    revenueApi,
    hygieneApi,
    arOver90Api,
    scheduleOperationsApi,
    insuranceClaimsApi,
    patientsAgeApi,
    patientStatusApi,
    seatUtilizationApi,
  }
}

export type OperationsDashboard = ReturnType<typeof useOperationsDashboardImpl>

export function useOperationsDashboard(
  options: UseOperationsDashboardOptions,
): OperationsDashboard {
  return useOperationsDashboardImpl(options)
}
